import { ChevronDown, ChevronRight, FileWarning, Folder, Lock, Play, Scissors } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { StagedEntry } from '@/lib/api';
import { formatBytes } from '@/lib/format';

/** The file tree an analyst picks from after a sample has been inspected.
 *
 *  Two things it must get right. Entries are addressed by id, never by name -
 *  a member name comes out of an archive somebody else built. And an entry
 *  whose bytes could not be read is shown with the reason rather than hidden,
 *  because "the archive is encrypted" is the answer the analyst needs. */

interface Props {
  entries: StagedEntry[];
  selected: Set<string>;
  /** Ceiling on how many entries may be picked, from the server. */
  maxPicks: number;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  disabled?: boolean;
}

/** One node of the tree, with its children resolved. */
interface TreeNode {
  entry: StagedEntry;
  children: TreeNode[];
}

/** Build the hierarchy from the flat list. The server returns parents before
 *  their children, so one pass is enough. */
function buildTree(entries: StagedEntry[]): TreeNode[] {
  const nodes = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];
  for (const entry of entries) {
    const node: TreeNode = { entry, children: [] };
    nodes.set(entry.id, node);
    const parent = entry.parent_id ? nodes.get(entry.parent_id) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

export default function StagedFileTree({
  entries,
  selected,
  maxPicks,
  onToggle,
  onSelectAll,
  onClear,
  disabled = false,
}: Props) {
  const tree = useMemo(() => buildTree(entries), [entries]);
  // Two different counts, and conflating them is what put "3 of 3 can be
  // detonated" above a row reading "an archive cannot be started".
  //
  // `selectable` means the entry HAS BYTES to submit - an archive does. Whether
  // the guest can start it is what `launch_note` answers: the backend leaves it
  // empty exactly when there is a detonator for the file, and fills it in with
  // the reason when there is not.
  const { submittable, detonatable } = useMemo(
    () => ({
      submittable: entries.filter((e) => e.selectable).length,
      detonatable: entries.filter((e) => e.selectable && !e.launch_note).length,
    }),
    [entries]
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  function toggleCollapse(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const atCeiling = selected.size >= maxPicks;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div>
          <strong>Contents</strong>
          <span className="text-muted text-sm" style={{ marginLeft: '8px' }}>
            {detonatable} of {submittable} can be detonated
          </span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onSelectAll}
            disabled={disabled || submittable === 0}
          >
            Select all
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClear}
            disabled={disabled || selected.size === 0}
          >
            Clear
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div role="tree" style={{ minWidth: '480px' }}>
          {tree.map((node) => (
            <TreeRow
              key={node.entry.id}
              node={node}
              parentName=""
              selected={selected}
              collapsed={collapsed}
              atCeiling={atCeiling}
              disabled={disabled}
              onToggle={onToggle}
              onCollapse={toggleCollapse}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  node: TreeNode;
  /** The parent's full display name, used to derive this row's leaf. */
  parentName: string;
  selected: Set<string>;
  collapsed: Set<string>;
  atCeiling: boolean;
  disabled: boolean;
  onToggle: (id: string) => void;
  onCollapse: (id: string) => void;
}

function TreeRow({
  node,
  parentName,
  selected,
  collapsed,
  atCeiling,
  disabled,
  onToggle,
  onCollapse,
}: RowProps) {
  const { entry, children } = node;
  const isSelected = selected.has(entry.id);
  const isCollapsed = collapsed.has(entry.id);
  const hasChildren = children.length > 0;
  // Why this entry is the way it is, in the server's words, most urgent first.
  // An entry with no bytes says so; one that has bytes and still will not start
  // says THAT, which is the difference between a run that produces a report and
  // one that produces an empty one; and an archive this build could not open
  // says so rather than looking like an archive with nothing in it.
  const note = entry.unavailable_reason || entry.launch_note || entry.description;
  // The ceiling blocks new picks but never blocks unpicking, or the analyst
  // could reach a state they cannot leave.
  const blocked = disabled || !entry.selectable || (atCeiling && !isSelected);

  return (
    <>
      <div
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={hasChildren ? !isCollapsed : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 16px',
          paddingLeft: `${16 + entry.depth * 20}px`,
          borderBottom: '1px solid var(--border-subtle)',
          opacity: entry.selectable ? 1 : 0.6,
          background: isSelected ? 'var(--status-info-bg)' : undefined,
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px' }}
            aria-label={isCollapsed ? `Expand ${entry.name}` : `Collapse ${entry.name}`}
            onClick={() => onCollapse(entry.id)}
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </button>
        ) : (
          <span style={{ width: '18px' }} />
        )}

        <input
          type="checkbox"
          checked={isSelected}
          disabled={blocked}
          onChange={() => onToggle(entry.id)}
          aria-label={`Detonate ${entry.name}`}
        />

        {entry.is_container ? (
          <Folder size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        ) : (
          <FileWarning size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        )}

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500, wordBreak: 'break-all' }}>
              {leafName(entry.name, parentName)}
            </span>
            {entry.detected_format && (
              <span className="badge">
                {entry.detected_format}
                {entry.pe_kind && entry.pe_kind !== 'program' ? ` ${entry.pe_kind}` : ''}
              </span>
            )}
            {entry.recommended && (
              <span className="badge" title="The program this upload is about; selected by default">
                <Play size={11} style={{ marginRight: '4px' }} />
                detonates
              </span>
            )}
            {entry.origin === 'carved' && (
              <span className="badge" title={`Found at offset ${entry.source_offset ?? 0}`}>
                <Scissors size={11} style={{ marginRight: '4px' }} />
                embedded
              </span>
            )}
            {entry.encrypted && (
              <span className="badge">
                <Lock size={11} style={{ marginRight: '4px' }} />
                encrypted
              </span>
            )}
          </div>
          <div className="text-muted text-sm">
            {formatBytes(entry.size)}
            {note && ` · ${note}`}
          </div>
          {entry.prior_analysis && (
            /* These exact bytes already have a report. Shown rather than
               enforced: the entry stays selectable, so a run on another
               operating system or after a failure is still one click. */
            <div className="text-sm" style={{ marginTop: '2px' }}>
              <Link to={`/submissions/${entry.prior_analysis.submission_id}`}>
                Already analysed
                {entry.prior_analysis.verdict ? ` - ${entry.prior_analysis.verdict}` : ''}
              </Link>
              <span className="text-muted">
                {' · '}
                {new Date(entry.prior_analysis.completed_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      </div>

      {!isCollapsed &&
        children.map((child) => (
          <TreeRow
            key={child.entry.id}
            node={child}
            parentName={entry.name}
            selected={selected}
            collapsed={collapsed}
            atCeiling={atCeiling}
            disabled={disabled}
            onToggle={onToggle}
            onCollapse={onCollapse}
          />
        ))}
    </>
  );
}

/** The server sends a full display path ("dropper.zip → payload.exe"); the tree
 *  already shows the parents, so a row shows only its own leaf.
 *
 *  Derived by removing the PARENT's path rather than by splitting on the
 *  separator. A member name comes out of an archive somebody else built and can
 *  contain the separator itself, which would make a split take the wrong half
 *  and show a misleading name for a file the analyst is deciding whether to
 *  detonate. Anything unexpected falls back to the full path - long, but never
 *  wrong. */
function leafName(path: string, parentName: string): string {
  if (!parentName) return path;
  const prefix = `${parentName} → `;
  return path.startsWith(prefix) ? path.slice(prefix.length) || path : path;
}
